import { useEffect } from 'react';

import {
  Assets,
  Button,
  ButtonTextOrIcon,
  Checkbox,
  Container,
  FAQ,
  Feedback,
  Panel,
  theme,
  useMediaMin,
} from '@foradevelopment/fora-ui';
import { PayRow } from '@fora-monorepo/employer-my-pages-common/types';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';
import { trackCustomEvent } from '@fora-monorepo/webapp-analytics';

import { goToReceipt, setReportButtonState, setReviewAndConfirmChecked } from '../../redux/payroll/payrollSlice';
import { reportPayroll } from '../../redux/payroll/payrollThunks';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { ReportSummary } from '../components/ReportSummary';
import { mapPayrollFormDataToPayRowList } from '../utils';
import { useTypedTranslation } from '../../i18n/i18n';

export const ReviewAndConfirmPanel = () => {
  const { t } = useTypedTranslation();

  const dispatch = useAppDispatch();
  const {
    reviewAndConfirmChecked,
    loadingReportPayroll,
    reviewAndConfirmPayReportPanelState,
    inputPayData,
    selectedPayMonthOverview: selectedReportingPeriod,
  } = useAppSelector(state => state.payroll);
  const faqs = useAppSelector(state => state.content.content.faqListReportPayrollConfirmStep);
  const isDesktop = useMediaMin(theme.breakpoint.lg);
  const reportButtonDisabled = useAppSelector(state => state.payroll.reportButtonDisabled);

  useEffect(() => {
    if (loadingReportPayroll === ThunkRequestStatus.Fulfilled) {
      dispatch(setReviewAndConfirmChecked({ checked: false }));
      dispatch(goToReceipt());
    }
    if (loadingReportPayroll === ThunkRequestStatus.Rejected) {
      dispatch(setReportButtonState(false));
    }
  }, [dispatch, loadingReportPayroll]);

  const handleConfirmButtonClick = async () => {
    if (reviewAndConfirmChecked) {
      dispatch(setReportButtonState(true));
      try {
        const payroll: PayRow[] = mapPayrollFormDataToPayRowList(inputPayData, selectedReportingPeriod?.payMonth);
        trackCustomEvent('Manual reporting - sent report', { payMonth: selectedReportingPeriod?.payMonth });
        await dispatch(reportPayroll({ payroll, payMonth: selectedReportingPeriod?.payMonth ?? '' }));
      } catch {
        dispatch(setReportButtonState(false));
      }
    }
  };

  const handleConfirmInfoCheckbox = () => {
    dispatch(setReviewAndConfirmChecked({ checked: !reviewAndConfirmChecked }));
  };

  return (
    <Panel
      {...reviewAndConfirmPayReportPanelState}
      testId="test-panel-review-confirm"
      previousPanelName="panel-paytable"
      name="panel-review-confirm"
    >
      <Panel.Header>{t('panel.reviewAndConfirm.panelHeader')}</Panel.Header>
      <Panel.Body>
        <Panel.MainSection>
          {selectedReportingPeriod && <ReportSummary selectedReportingPeriod={selectedReportingPeriod} />}
          <Container marginTop={60} marginBottom={60}>
            <Checkbox
              id="review-and-confirm-checkbox"
              checked={reviewAndConfirmChecked}
              onChange={handleConfirmInfoCheckbox}
              topAlignCheckbox={true}
            >
              {t('panel.reviewAndConfirm.checkboxDescription')}
            </Checkbox>
          </Container>
          <Button
            onClick={handleConfirmButtonClick}
            disabled={!reviewAndConfirmChecked || reportButtonDisabled}
            variant={reviewAndConfirmChecked ? 'primary' : 'grey'}
          >
            <ButtonTextOrIcon
              icon={<Assets.Spinner center={false} width={20} color="white" />}
              textColor={reviewAndConfirmChecked ? 'white' : 'black70'}
              isLoading={loadingReportPayroll === ThunkRequestStatus.Pending}
            >
              {t('common.confirm')}
            </ButtonTextOrIcon>
          </Button>
          {loadingReportPayroll === ThunkRequestStatus.Rejected && (
            <Feedback type="error" marginTop={60}>
              {t('errorMessages.generic.message')}
            </Feedback>
          )}
        </Panel.MainSection>
        <Panel.AsideSection>
          {faqs.length > 0 && isDesktop && <FAQ testId="review-confirm-faq" layout="side" questionAnswers={faqs} />}
        </Panel.AsideSection>
        {faqs.length > 0 && !isDesktop && <FAQ testId="review-confirm-faq" layout="modal" questionAnswers={faqs} />}
      </Panel.Body>
    </Panel>
  );
};
